import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Film, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MediaUploader, MediaPreview } from "@/components/MediaUploader";
import { toast } from "sonner";

function bytes(n) {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function MyMedia() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !user) navigate("/login");
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (user) {
            api.get("/media/me")
                .then((r) => setItems(r.data?.items || []))
                .finally(() => setLoading(false));
        }
    }, [user]);

    async function remove(id) {
        if (!window.confirm("Delete this media? This cannot be undone.")) return;
        try {
            await api.delete(`/media/${id}`);
            setItems((prev) => prev.filter((m) => m.id !== id));
            toast.success("Media deleted");
        } catch (e) {
            toast.error("Delete failed");
        }
    }

    if (!user) return null;

    return (
        <div className="ok-container py-10 max-w-5xl" data-testid="my-media-page">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-3">Member Media · UNVEILED</div>
            <h1 className="font-display text-3xl">My Media Library</h1>
            <p className="opacity-70 mt-2 text-sm leading-relaxed max-w-2xl">
                Upload photos and videos that attach to your encounter submissions, comments, and profile. Files persist
                permanently and are served from stable URLs. They survive deploys, restarts, and cache refreshes.
            </p>

            <div className="mt-6">
                <MediaUploader onUploaded={(m) => setItems((p) => [m, ...p])} testId="my-media-uploader" />
            </div>

            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-10 mb-3">
                Your uploads ({items.length})
            </div>

            {loading ? (
                <div className="text-xs font-mono opacity-50">Loading library…</div>
            ) : items.length === 0 ? (
                <div className="ok-card p-8 text-center" data-testid="my-media-empty">
                    <ImageIcon className="w-8 h-8 mx-auto opacity-30 mb-2" />
                    <p className="text-sm opacity-70">No uploads yet. Drop a photo or video above to get started.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="my-media-grid">
                    {items.map((m) => (
                        <div key={m.id} className="ok-card p-3 flex flex-col" data-testid={`my-media-item-${m.id}`}>
                            <div className="aspect-square bg-black rounded overflow-hidden flex items-center justify-center">
                                <MediaPreview media={m} />
                            </div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-wide opacity-60 flex items-center gap-1">
                                {m.media_type === "video" ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                                {m.media_type} · {bytes(m.size)}
                            </div>
                            <div className="text-xs mt-1 truncate" title={m.filename}>{m.filename}</div>
                            <button
                                onClick={() => remove(m.id)}
                                className="ok-btn-outline text-xs mt-2 inline-flex items-center justify-center gap-1"
                                data-testid={`my-media-delete-${m.id}`}
                            >
                                <Trash2 className="w-3 h-3" /> Delete
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
